import { forwardRef } from 'react';
import { Box, Typography, Table, TableBody, TableCell, TableHead, TableRow, Divider } from '@mui/material';

const fmt = (n) => new Intl.NumberFormat('en-PK').format(Math.round(n || 0));
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-PK') : '—';

const PurchaseGRN = forwardRef(function PurchaseGRN({ purchase, tenant }, ref) {
  if (!purchase) return null;
  const items = purchase.items || [];

  return (
    <Box ref={ref} sx={{ p: 4, fontFamily: 'Arial, sans-serif', maxWidth: 800, mx: 'auto' }}>
      <style>{`@media print { @page { size: A4; margin: 15mm; } body { font-size: 12px; } }`}</style>

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={700} color="primary">{tenant?.name || 'Business Name'}</Typography>
          <Typography variant="body2" color="text.secondary">{tenant?.address}</Typography>
          <Typography variant="body2" color="text.secondary">{tenant?.phone} · {tenant?.email}</Typography>
          {tenant?.tax_number && <Typography variant="body2" color="text.secondary">NTN/GST: {tenant.tax_number}</Typography>}
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          <Typography variant="h6" fontWeight={700} color="primary.dark">GOODS RECEIVED NOTE</Typography>
          <Typography variant="body2" color="text.secondary">GRN / Purchase Receipt</Typography>
          <Box sx={{ mt: 1, p: 1, border: '1px solid', borderColor: 'primary.main', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary">GRN #</Typography>
            <Typography variant="h6" fontFamily="monospace" fontWeight={700} color="primary">
              GRN-{String(purchase.id).padStart(6, '0')}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Divider sx={{ mb: 2 }} />

      {/* Supplier + Date info */}
      <Box sx={{ display: 'flex', gap: 4, mb: 3 }}>
        <Box sx={{ flex: 1, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={700} display="block" gutterBottom>SUPPLIER</Typography>
          <Typography fontWeight={700}>{purchase.supplier_name || 'Walk-in / Cash Purchase'}</Typography>
          {purchase.supplier_phone && <Typography variant="body2">{purchase.supplier_phone}</Typography>}
          {purchase.supplier_address && <Typography variant="body2" color="text.secondary">{purchase.supplier_address}</Typography>}
        </Box>
        <Box sx={{ flex: 1, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={700} display="block" gutterBottom>RECEIPT DETAILS</Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="body2" color="text.secondary">Date:</Typography>
            <Typography variant="body2" fontWeight={600}>{fmtDate(purchase.created_at)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="body2" color="text.secondary">Received by:</Typography>
            <Typography variant="body2" fontWeight={600}>{purchase.created_by_name || '—'}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="body2" color="text.secondary">Payment:</Typography>
            <Typography variant="body2" fontWeight={600} sx={{ textTransform: 'capitalize' }}>{purchase.payment_status}</Typography>
          </Box>
        </Box>
      </Box>

      {/* Items table */}
      <Table size="small" sx={{ mb: 2, '& th': { fontWeight: 700, bgcolor: '#f3f4f6' } }}>
        <TableHead>
          <TableRow>
            <TableCell>#</TableCell>
            <TableCell>Product Name</TableCell>
            <TableCell>Batch</TableCell>
            <TableCell>Expiry</TableCell>
            <TableCell align="right">Qty</TableCell>
            <TableCell align="right">Unit Price</TableCell>
            <TableCell align="right">Total</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((item, i) => (
            <TableRow key={i}>
              <TableCell sx={{ color: 'text.secondary' }}>{i + 1}</TableCell>
              <TableCell><Typography fontWeight={600}>{item.product_name}</Typography></TableCell>
              <TableCell><Typography variant="body2" fontFamily="monospace">{item.batch_number || '—'}</Typography></TableCell>
              <TableCell><Typography variant="body2">{fmtDate(item.expiry_date)}</Typography></TableCell>
              <TableCell align="right"><Typography fontWeight={700}>{item.quantity}</Typography></TableCell>
              <TableCell align="right">₨ {fmt(item.unit_price)}</TableCell>
              <TableCell align="right"><Typography fontWeight={700}>₨ {fmt(item.total_price)}</Typography></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Totals */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
        <Box sx={{ minWidth: 240 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
            <Typography color="text.secondary">Subtotal:</Typography>
            <Typography fontWeight={600}>₨ {fmt(purchase.subtotal || purchase.total_amount)}</Typography>
          </Box>
          <Divider />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.75, bgcolor: '#f3f4f6', px: 1, mt: 0.5, borderRadius: 1 }}>
            <Typography fontWeight={700} fontSize={16}>TOTAL:</Typography>
            <Typography fontWeight={700} fontSize={16} color="primary.main">₨ {fmt(purchase.total_amount)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
            <Typography color="success.main">Amount Paid:</Typography>
            <Typography color="success.main" fontWeight={600}>₨ {fmt(purchase.paid_amount)}</Typography>
          </Box>
          {+purchase.due_amount > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
              <Typography color="error.main">Balance Due:</Typography>
              <Typography color="error.main" fontWeight={700}>₨ {fmt(purchase.due_amount)}</Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* Notes */}
      {purchase.notes && (
        <Box sx={{ mb: 3, p: 1.5, bgcolor: '#fffbeb', borderRadius: 1, border: '1px solid #fcd34d' }}>
          <Typography variant="caption" fontWeight={700}>Notes: </Typography>
          <Typography variant="body2">{purchase.notes}</Typography>
        </Box>
      )}

      {/* Footer */}
      <Divider sx={{ mb: 2 }} />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <Box>
          <Typography variant="caption" color="text.secondary">
            Printed: {new Date().toLocaleString('en-PK')}
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'center' }}>
          <Box sx={{ borderTop: '1px solid #999', width: 180, mb: 0.5 }} />
          <Typography variant="caption" color="text.secondary">Authorized Signature</Typography>
        </Box>
      </Box>
    </Box>
  );
});

export default PurchaseGRN;
